import { useCheckout } from '@stripe/react-stripe-js/checkout';
import { useMemo } from 'react';

export type CheckoutResult = ReturnType<typeof useCheckout>;

/** Hook to determine if payment is required for the checkout.
 *
 * This smooths over when the checkout is being submitted and `canSubmit` changes to false.
 */
export function useIsPaymentRequiredForCheckout(checkout: CheckoutResult): boolean {
  return useMemo(() => isPaymentRequiredForCheckout(checkout), [checkout.type]);
}

/**
 * True when the checkout requires payment. False when no payment is required
 */
export function isPaymentRequiredForCheckout(checkout: CheckoutResult): boolean {
  if (checkout.type !== 'success') return true;

  const { total, discountAmounts, canConfirm } = checkout.checkout;

  const totalIsZero = total?.total?.minorUnitsAmount === 0;
  const has100PercentOffForeverDiscount =
    discountAmounts?.some(
      (discount) => discount.percentOff === 100 && discount.recurring?.type !== 'repeating'
    ) ?? false;

  // It's easier to check for when a payment is NOT required
  const paymentNotRequired = canConfirm && totalIsZero && has100PercentOffForeverDiscount;

  return !paymentNotRequired;
}
